import { XCircleIcon } from "@heroicons/react/outline";
import { Fragment, useEffect, useState } from "react";
import { useAsync } from "react-async";
import { useHistory, useParams } from "react-router-dom";
import CascadingVariableOverride from "../../../api/models/cascadingVariableOverride";
import CascadingVariableOverrideOptions from "../../../api/models/cascadingVariableOverrideOptions";
import { addEditCascadingVariable, deleteCascadingVariable, getCascadingVariableById, GetCascadingVariableOverrideOptions } from "../../../api/services/cascadingVariablesService";
import Layout from "../../../components/layout";
import { ResponseStatus } from "../../../components/notifications";
import SubmitButton from "../../../components/submitButton";
import Select from 'react-select';
import { graySelectStyles } from '../../../common/selectStyles';

type Params = {
    id: string;
};

const initializeData = async ({ id }: any) => {
    //ignore new 
    if (id === "new")
        return new Promise<void>((resolve) => { resolve(); }); //need to call resolve otherwise the loading will always be true

    return await getCascadingVariableById(Number(id)); //fetch the data from the API 
}

const EditCascadingVariable = () => {
    const history = useHistory();
    const { id } = useParams<Params>();
    const [name, setName] = useState<string>();
    const [defaultValue, setDefaultValue] = useState<number>(0);
    const [options, setOptions] = useState<CascadingVariableOverrideOptions>();
    const [overrides, setOverrides] = useState<CascadingVariableOverride[]>([]);

    let isLoading: boolean = false;

    //API Crud operations
    const initAsync = useAsync({
        promiseFn: initializeData,
        id: id,
        onResolve: (res) => {
            //make sure we have the right response then update the state.
            if (typeof res !== "undefined" && res.status === 200) {
                setName(res.data.variableName);
                setDefaultValue(res.data.defaultValue);
                setOverrides(res.data.overrides);
            }
        }
    });
    if (initAsync.isLoading) isLoading = true;

    const optionsAsync = useAsync({
        promiseFn: GetCascadingVariableOverrideOptions,
        onResolve: (res) => {
            if (typeof res.data !== "undefined") {
                setOptions(res.data);
            }
        }
    })
    if (optionsAsync.isLoading) isLoading = true;

    const save = async () => {
        if (typeof name === "undefined")
            return Promise.reject('Please enter a name');

        if (name.length >= 50)
            return Promise.reject('The name must be less than 50 characters');

        if (defaultValue < 0)
            return Promise.reject('The default value must be a positive number');

        if (defaultValue > 999)
            return Promise.reject('The default value must be less than 999');

        //validate overrides if they exist
        if (overrides.length > 0) {
            if (overrides.some(o => o.value < 0))
                return Promise.reject('Please make sure all overrides have a positive value');

            if (overrides.some(o => o.value > 999))
                return Promise.reject('Please make sure all override values are less than 999');

            if (overrides.some(o => (
                (o.divisionId ?? -1) < 0 &&
                (o.districtId ?? -1) < 0 &&
                (o.programId ?? -1) < 0)))
                return Promise.reject('All overrides must have a division selected');
        }


        //build out the overrides
        let newOverrides: CascadingVariableOverride[] = [];
        overrides.forEach((o) => {
            if ((o.districtId! > 0 || o.divisionId! > 0 || o.programId! > 0) && o.value > 0) {
                newOverrides.push({
                    cascadingVariableOverrideId: o.cascadingVariableOverrideId,
                    divisionId: o.divisionId ?? -1,
                    districtId: o.districtId ?? -1,
                    programId: o.programId ?? -1,
                    value: o.value
                });
            }
        });

        addEditCascadingVariable({
            cascadingVariableId: (id === "new") ? -1 : Number(id).valueOf(),
            variableName: name ?? "",
            defaultValue: defaultValue,
            isActive: true,
            overrides: overrides
        });

        //redirect the user after 1 second
        setTimeout(() => {
            history.replace("/admin/proofing");
        }, 1000);
    }
    const saveAsync = useAsync({ deferFn: save });
    if (saveAsync.isLoading) isLoading = true;

    const remove = async () => {
        //remove the var is it's not new
        if (id !== "new") {
            await deleteCascadingVariable(Number(id));
        }

        //redirect the user after 1 second
        setTimeout(() => {
            history.replace("/admin/proofing");
        }, 1000);
    }
    const removeAsync = useAsync({ deferFn: remove });
    if (removeAsync.isLoading) isLoading = true;
    //end of API logic

    const AddOverride = () => {
        overrides.push({
            cascadingVariableOverrideId: -1,
            divisionId: -1,
            districtId: -1,
            programId: -1,
            value: 0
        });
        setOverrides([...overrides]);
    }

    const OverrideOptionSelected = (index: number, option: string, value: number) => {
        if (typeof overrides === "undefined")
            return;

        if (option === "division") {
            overrides[index].divisionId = value;
            overrides[index].districtId = -1;
            overrides[index].programId = -1;
        }

        if (option === "discipline") {
            overrides[index].disciplineId = value;
        }

        if (option === "district") {
            let district = options?.districts.find(d => d.districtId === value);
            overrides[index].divisionId = district?.divisionId;
            overrides[index].districtId = value;
            overrides[index].programId = -1;
        }

        if (option === "program") {
            let program = options?.programs.find(d => d.programId === value);
            let district = options?.districts.find(d => d.districtId === program?.districtId);

            overrides[index].divisionId = district?.divisionId;
            overrides[index].districtId = program?.districtId;
            overrides[index].programId = value;
        }

        setOverrides([...overrides]);
    }

    const OverrideValueUpdated = (index: number, value: number) => {
        overrides[index].value = value;
        setOverrides([...overrides]);
    }

    const RemoveOverrideOption = (index: number) => {
        overrides.splice(index, 1);
        setOverrides([...overrides]);
    }

    useEffect(() => {
        //AddOverride();
    }, [setOverrides]);

    return (
        <Layout isLoading={isLoading}>
            <Fragment>
                <ResponseStatus state={saveAsync} />
                <ResponseStatus state={removeAsync} />

                {!removeAsync.isResolved && !saveAsync.isResolved && (
                    <div
                        className="ml-5 mr-5">
                        <label>
                            Name
                            <input
                                className="mr-1"
                                type="text"
                                name="varName"
                                defaultValue={name}
                                onChange={(e) => { setName(e.target.value) }}
                            />
                        </label>

                        <div className="w-32">
                            <label>
                                Default Value
                                <input
                                    type="number"
                                    value={defaultValue}
                                    onChange={(e) => { setDefaultValue(Number(e.target.value)); }} />
                            </label>
                        </div>

                        <div className="mt-5 ml-10 mr-10 mb-5">
                            <h2>Overrides</h2>
                            <table
                                className="w-full">
                                <thead>
                                    <tr>
                                        <th>Division</th>
                                        <th>District</th>
                                        <th>Program</th>
                                        <th>Discipline</th>
                                        <th>Override Value</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {overrides?.map((override, oi) => (
                                        <tr key={"over_" + oi}>
                                            <td>
                                                <Select
                                                    className={(typeof override.divisionId !== "undefined" && override.divisionId > 0) ? "" : "bg-gray-200"}
                                                    onChange={(selected) => { OverrideOptionSelected(oi, "division", Number(selected?.value ?? -1)); }}
                                                    value={[
                                                        { value: -1, label: 'none' },
                                                        ...(options?.divisions.sort((a, b) => a.name.localeCompare(b.name)).map(d => ({ value: d.divisionId, label: d.name })) || [])
                                                    ].find(o => o.value === (override.divisionId ?? -1)) || null}
                                                    options={[
                                                        { value: -1, label: 'none' },
                                                        ...(options?.divisions.sort((a, b) => a.name.localeCompare(b.name)).map(d => ({ value: d.divisionId, label: d.name })) || [])
                                                    ]}
                                                    styles={graySelectStyles}
                                                />
                                            </td>
                                            <td>
                                                <Select
                                                    className={(typeof override.districtId !== "undefined" && override.districtId > 0) ? "" : "bg-gray-200"}
                                                    onChange={(selected) => { OverrideOptionSelected(oi, "district", Number(selected?.value ?? -1)); }}
                                                    value={[
                                                        { value: -1, label: 'none' },
                                                        ...(options?.districts.filter(d => override.divisionId === -1 || d.divisionId === override.divisionId).sort((a, b) => a.name.localeCompare(b.name)).map(d => ({ value: d.districtId, label: d.name })) || [])
                                                    ].find(o => o.value === (override.districtId ?? -1)) || null}
                                                    options={[
                                                        { value: -1, label: 'none' },
                                                        ...(options?.districts.filter(d => override.divisionId === -1 || d.divisionId === override.divisionId).sort((a, b) => a.name.localeCompare(b.name)).map(d => ({ value: d.districtId, label: d.name })) || [])
                                                    ]}
                                                    styles={graySelectStyles}
                                                />
                                            </td>
                                            <td>
                                                <Select
                                                    className={(typeof override.programId !== "undefined" && override.programId > 0) ? "" : "bg-gray-200"}
                                                    onChange={(selected) => { OverrideOptionSelected(oi, "program", Number(selected?.value ?? -1)); }}
                                                    value={[
                                                        { value: -1, label: 'none' },
                                                        ...(options?.programs.filter(p => override.districtId === -1 || p.districtId === override.districtId).sort((a, b) => a.name.localeCompare(b.name)).map(p => ({ value: p.programId, label: p.name })) || [])
                                                    ].find(o => o.value === (override.programId ?? -1)) || null}
                                                    options={[
                                                        { value: -1, label: 'none' },
                                                        ...(options?.programs.filter(p => override.districtId === -1 || p.districtId === override.districtId).sort((a, b) => a.name.localeCompare(b.name)).map(p => ({ value: p.programId, label: p.name })) || [])
                                                    ]}
                                                    styles={graySelectStyles}
                                                />
                                            </td>
                                            <td>
                                                <Select
                                                    className={(typeof override.disciplineId !== "undefined" && override.disciplineId > 0) ? "" : "bg-gray-200"}
                                                    onChange={(selected) => { OverrideOptionSelected(oi, "discipline", Number(selected?.value ?? -1)); }}
                                                    value={[
                                                        { value: -1, label: 'none' },
                                                        ...(options?.disciplines.sort((a, b) => a.name.localeCompare(b.name)).map(d => ({ value: d.disciplineId, label: d.name })) || [])
                                                    ].find(o => o.value === (override.disciplineId ?? -1)) || null}
                                                    options={[
                                                        { value: -1, label: 'none' },
                                                        ...(options?.disciplines.sort((a, b) => a.name.localeCompare(b.name)).map(d => ({ value: d.disciplineId, label: d.name })) || [])
                                                    ]}
                                                    styles={graySelectStyles}
                                                />
                                            </td>
                                            <td
                                                className="w-32">
                                                <input
                                                    type="number"
                                                    value={override.value}
                                                    onChange={(e) => { OverrideValueUpdated(oi, Number(e.target.value)); }} />
                                            </td>
                                            <td>
                                                <XCircleIcon
                                                    className="w-7 ml-1 text-red-800 cursor-pointer"
                                                    onClick={() => { RemoveOverrideOption(oi); }} />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            <button
                                className="m-auto block mt-3 bg-transparent border border-green-600 border-solid text-green-700 hover:text-white"
                                onClick={() => { AddOverride(); }}>Add Override
                            </button>
                        </div>

                        <div className='flex justify-between mt-5'>
                            <SubmitButton
                                className={(id === "new") ? "opacity-25 secondary" : "secondary"}
                                disabled={id === "new"}
                                text='Remove'
                                onClick={() => { removeAsync.run(); }} />

                            <SubmitButton
                                text={(id === "new") ? "Create Variable" : "Save Variable"}
                                onClick={() => { saveAsync.run(); }} />
                        </div>
                    </div>
                )}
            </Fragment>
        </Layout>
    )
}

export default EditCascadingVariable;